import NumberInput from '../components/NumberInput.jsx'
import { useState, useEffect } from 'react'
import { useAccounts } from '../hooks/useAccounts'
import { useSettings } from '../hooks/useSettings'
import { fmt, calcShares, calcUnifiedStop, calcProfitTarget, checkStopBreach } from '../lib/calculations'

export default function Calculator() {
  const { accounts } = useAccounts()
  const { settings } = useSettings()
  const [entries, setEntries] = useState([])
  const [costBasis, setCostBasis] = useState('fifo')

  const numTranches = settings.default_tranches
  const allocPct = settings.default_alloc_pct
  const riskPct = settings.default_risk_pct

  useEffect(() => {
    setEntries(prev => {
      const next = Array(numTranches).fill(null)
      prev.forEach((v, i) => { if (i < numTranches) next[i] = v })
      return next
    })
  }, [numTranches])

  const primaryAccount = accounts.find(a => (a.equity || 0) > 0)
  const perTranche = primaryAccount ? primaryAccount.equity * (allocPct / 100) : 0

  const filledTranches = entries
    .map((price, i) => price > 0 ? { idx: i, price, shares: calcShares(perTranche, price) } : null)
    .filter(Boolean)

  const stopCalc = calcUnifiedStop(filledTranches, riskPct)
  const profitTarget = stopCalc ? calcProfitTarget(stopCalc, filledTranches) : null
  const isBreached = stopCalc ? checkStopBreach(stopCalc) : false

  const rowStops = {}
  for (let i = 0; i < numTranches; i++) {
    const cumTranches = entries
      .slice(0, i + 1)
      .map((price, j) => price > 0 ? { idx: j, price, shares: calcShares(perTranche, price) } : null)
      .filter(Boolean)
    if (cumTranches.length === i + 1) {
      const calc = calcUnifiedStop(cumTranches, riskPct)
      if (calc) rowStops[i] = calc.stopPrice
    }
  }

  const reset = () => setEntries(Array(numTranches).fill(null))

  const tagLabels = ['Initial Buy', 'Add-On 2', 'Add-On 3', 'Add-On 4', 'Add-On 5']
  const tagColors = [
    'bg-ink/10 text-ink border border-ink/20',
    'bg-forest/10 text-forest border border-forest/20',
    'bg-forest/10 text-forest border border-forest/20',
    'bg-forest/10 text-forest border border-forest/20',
    'bg-forest/10 text-forest border border-forest/20',
  ]

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Tool</div>
          <h1 className="font-black text-2xl uppercase tracking-wide text-ink">$ Stop Calculator</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-canvas2 border border-border rounded-lg p-1">
            <span className="font-mono text-[10px] text-inkMuted px-2">Cost Basis</span>
            {['fifo', 'lifo'].map(m => (
              <button key={m} onClick={() => setCostBasis(m)}
                className={`font-mono text-xs px-3 py-1.5 rounded-md transition-all ${
                  costBasis === m ? 'bg-ink text-parchment' : 'text-inkMuted hover:text-ink'
                }`}>
                {m.toUpperCase()}
              </button>
            ))}
          </div>
          <button onClick={reset} className="btn-secondary text-xs py-2">↺ Reset</button>
        </div>
      </div>
      <div className="bg-canvas border border-border rounded-xl overflow-hidden mb-4">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Position Builder</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">Build Your Position</h2>
          <p className="font-mono text-xs text-inkMuted mt-1">
            Enter entry price for each tranche. Stop recalculates across the <em>entire</em> position.
          </p>
        </div>
        <div>
          {Array.from({ length: numTranches }).map((_, i) => {
            const price = entries[i]
            const stop = rowStops[i]
            return (
              <div key={i} className={`px-6 py-4 border-b border-border last:border-0 grid grid-cols-1 sm:grid-cols-4 gap-4 items-center ${i > 0 && !entries[i - 1] ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider ${tagColors[i]}`}>
                    {tagLabels[i]}
                  </span>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-inkMuted uppercase tracking-wider mb-1.5">Entry Price</div>
                  <div className="flex items-center gap-1.5 bg-canvas2 border border-border rounded-lg px-3 py-2 focus-within:border-ink transition-colors">
                    <span className="font-mono text-xs text-inkMuted">$</span>
                    <NumberInput
                      allowEmpty step="0.01" min="0.01" placeholder="0.00"
                      value={price ?? null}
                      onValueChange={val => {
                        setEntries(prev => { const next = [...prev]; next[i] = val; return next })
                      }}
                      className="bg-transparent font-mono text-sm text-ink outline-none w-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-inkMuted uppercase tracking-wider mb-1.5">Dollar Stop</div>
                  <div className={`font-mono text-xl font-semibold ${stop ? 'text-ember' : 'text-inkMuted'}`}>
                    {stop ? fmt(stop) : '——'}
                  </div>
                  <div className="font-mono text-[10px] text-inkMuted mt-0.5">
                    {stop && price ? ((price - stop) / price * 100).toFixed(1) + '% below entry' : '—'}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-inkMuted uppercase tracking-wider mb-1.5">Shares Per Account</div>
                  <div className="flex gap-3 flex-wrap">
                    {accounts.map((acct, ai) => {
                      const acctPer = (acct.equity || 0) * (allocPct / 100)
                      const shares = price > 0 && acct.equity > 0 ? calcShares(acctPer, price) : null
                      return (
                        <div key={ai} className="text-center">
                          <div className="font-mono text-[9px] text-inkMuted uppercase tracking-wider mb-0.5">{acct.name}</div>
                          <div className={`font-mono text-sm font-bold ${shares ? 'text-ink' : 'text-inkMuted'}`}>
                            {shares ?? '——'}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {isBreached && stopCalc && (
        <div className="bg-ember/5 border border-ember/30 rounded-xl px-5 py-4 mb-4 font-mono text-xs">
          <div className="font-bold text-ember tracking-widest uppercase mb-1">
            ⚠ Stop Distance Breach — Max {riskPct}%
          </div>
          <div className="text-inkMuted">
            Stop ({fmt(stopCalc.stopPrice)}) is <strong className="text-ember">{stopCalc.stopDistPct.toFixed(1)}%</strong> below avg cost of {fmt(stopCalc.avgCost)}.
          </div>
        </div>
      )}
      {profitTarget && stopCalc && (
        <div className="bg-forest/5 border border-forest/20 rounded-xl px-5 py-4 font-mono">
          <div className="text-[10px] tracking-widest uppercase text-forest mb-3">
            ⟳ 2× Trade Risk Profit Target — Sell ⅓ of Position
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Target Price</div>
              <div className="text-xl font-semibold text-forest">{fmt(profitTarget.targetPrice)}</div>
              <div className="text-[10px] text-inkMuted">+{profitTarget.pctGainFromAvg.toFixed(1)}% from avg cost</div>
            </div>
            <div>
              <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Shares to Sell</div>
              <div className="text-xl font-semibold text-ink">{profitTarget.sharesToSell.toLocaleString()}</div>
              <div className="text-[10px] text-inkMuted">⅓ of {stopCalc.totalShares} total · {fmt(profitTarget.sellValue)}</div>
            </div>
            <div>
              <div className="text-[10px] text-inkMuted uppercase tracking-wider mb-1">Position Gain</div>
              <div className="text-xl font-semibold text-forest">+{fmt(profitTarget.gainAtTarget)}</div>
              <div className="text-[10px] text-inkMuted">= 2× your {fmt(stopCalc.actualMaxLoss)} risk</div>
            </div>
          </div>
          <div className="border-t border-border/50 pt-3">
            <div className="text-[9px] tracking-widest uppercase text-inkMuted mb-2">Shares to Sell Per Account</div>
            <div className="flex gap-5 flex-wrap">
              {accounts.map((acct, ai) => {
                const acctPer = (acct.equity || 0) * (allocPct / 100)
                const acctTotal = filledTranches.reduce((sum, t) => sum + calcShares(acctPer, t.price), 0)
                const acctSell = acct.equity > 0 ? Math.round(acctTotal / 3) : null
                return (
                  <div key={ai} className="text-center">
                    <div className="text-[9px] text-inkMuted uppercase tracking-wider mb-0.5">{acct.name}</div>
                    <div className={`text-sm font-bold ${acctSell ? 'text-ink' : 'text-inkMuted'}`}>
                      {acctSell ?? '——'}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
